import { computed, ref } from "@vue/composition-api";
import store from "@/store";
import axios from "@/libs/axios";
import {debounce} from "lodash";
import { useToast } from "vue-toastification/composition";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import {EventBus} from "@/libs/event-bus";
import { getCurrentInstance } from "@vue/composition-api";

export function useTrainingForm(emit) {
  const vm = getCurrentInstance().proxy;
  const toast = useToast();
  const { t } = useI18nUtils();

  const mapOptions = (item) => ({
    value: item.id,
    label: `${item.sirh ?? ''}-${item.description}`,
    categories: [item?.category],
    region: item?.region?.name,
    description: item.description,
    subregion: item?.sector?.name,
    sirh: item?.sirh,
    municipality: item?.municipality?.name,
    rems: item?.rems,
    id_seproban: item?.id_seproban,
  });
  const datePickerConfig = {
    maxDate: new Date(),
    dateFormat: "d/m/Y",
    allowInput: false,
    locale: {
      firstDayOfWeek: 1,
    }
  };
  const typeTrainingOptions = [
    "Programada",
    "Extraordinaria"
  ];
  const trainingModalityOptions = [
    "Presencial",
    "Remota"
  ];
  const trainingTopicsOptions = [
    "Protección Civil",
    "Safety",
    "Seguridad Física",
    "Control de accesos / Gerente Jr",
  ];

  const eventTypeOptions = [
    "Real",
    "Prueba",
  ];
  const resultsOptions = [
    "Aprobado",
    "Fallido",
    "No realizado",
  ];
  const defaulParticipantsData = {
    rems: null,
    name: null,
    number_of_participants: 0
  }
  const user = store.getters["user/getUser"];
  const translations = {
    "operational_security.evaluation.error_send_form": t("operational_security.evaluation.error_send_form"),
    "operational_security.evaluation.success_send_form": t("operational_security.evaluation.success_send_form"),
    "operational_security.evaluation.answers_successfully": t("operational_security.evaluation.answers_successfully"),
    "operational_security.evaluation.answers_error": t("operational_security.evaluation.answers_error"),
    "operational_security.evaluation.review_successfully": t("operational_security.evaluation.review_successfully"),
    "operational_security.evaluation.review_error": t("operational_security.evaluation.review_error"),
    "operational_security.evaluation.status_successfully": t("operational_security.evaluation.status_successfully"),
    "operational_security.evaluation.status_error": t("operational_security.evaluation.status_error"),
    "operational_security.evaluation.reopen_successfully": t("operational_security.evaluation.reopen_successfully"),
    "operational_security.evaluation.reopen_error": t("operational_security.evaluation.reopen_error"),
    "testing.penetration.required": t("testing.penetration.required"),
    "testing.penetration.perticipantRequired": t("testing.penetration.perticipantRequired"),
  };

  const requiredFields = ref([
    'id_center',
    'id_sector',
    'id_region',
    'registration_date',
    'training_type',
    'training_modality',
    'training_topic',
    'description_training',
    'participants'
  ]);

  const form = ref({
    id_center: null,
    id_sector: null,
    id_region: null,
    reference_number: null,
    registration_date: null,
    training_type: null,
    training_modality: null,
    training_topic: null,
    description_training: null,
    participants: [],
    attachedDocuments: [],
    lastStatus: null,
    trainingValidations: []
  });

  const currentFacilitySelected = ref(null);
  const facilityOptions = ref([]);
  const isFormDisabled = ref(false);
  const facilitySearch = ref(null);
  const files = ref([]);
  const existingFiles = ref([]); // when edit
  const deletedFiles = ref([]); // when edit
  const isEdit = ref(false);
  const formId = ref(false);
  const errors = ref({});
  const userSelected = ref(null);
  const userSelectedReasigned = ref(null);
  const userOptions = ref([]);
  const userReopenedOptions = ref([]);
  const validationComment = ref(null);
  const errorValidationComment = ref(null);
  const currentFormStatus = ref(null);
  const isFormFromSameUser = ref(true);
  const referenceShowed = ref("CA-AA-000000");
  const showAlert = ref(false);
  const successProcess = ref(false);
  const approverOptions = ref([]);
   const disabledButton = ref(false);

  const isDark = computed(() => store.state.appConfig.layout.skin === "dark");
  const formattedFacilityOptions = computed(() => {
    return (facilityOptions.value || [])
      .slice()
      .map(mapOptions);
  });
  const formStatus = computed(() => {
    return {
      withoutValue: false ,
      inProgress: currentFormStatus.value === 'in_progress',
      isRejected: currentFormStatus.value === 'rejected',
      isApproved: currentFormStatus.value === 'approved',
      isReview: currentFormStatus.value === 'in_review',
      isReopen: currentFormStatus.value === 'reopened',
      isCanceled: currentFormStatus.value === 'canceled'
    };
  });

  const canUserSendToReview = computed(() =>  {
    const conditionForForm    = (formStatus.value.inProgress || formStatus.value.isReopen);
    const userCanSendToReview = vm.$can('send_to_review', 'Trainings');
    const userIsOwnerForm     = (form.value.lastStatus && (form.value.lastStatus.id_user === user.id));

    return (conditionForForm && userCanSendToReview && userIsOwnerForm && successProcess.value);
  });

  const formattedUserOptions = computed(() =>
    (approverOptions.value || []).map(u => ({
      value: u.username || u.id,
      label: `${u.username} - ${u.full_name}`,
      id: u.id,
    }))
  );

  const onSearchFacility = debounce(async (query, loading) => {
    if (query && query.length) {
      loading(true);
      facilitySearch.value = query;
      await fetchFacilityData();
      loading(false);
    } else {

      facilitySearch.value = '';
      facilityOptions.value = [];
    }
  }, 500);

  function isMasterAdmOrSuperUser(profile) {
    const p = profile.toLowerCase()
    return p.includes('masteradmin') || p.includes('superusuario') || p.includes('supervisor')
  }

  function buildUserOptionLabel() {
    const parts = [user.user, user.full_name].filter(Boolean);
    return parts.join(" | ");
  }

  async function fetchFacilityData() {
    try {
      const excludedRems = form.value.participants.filter(
        p => p.rems
      )
      .map(p => p.rems);

      const {data} = await axios.get('/v1/unit', {
        params: {
          rems: facilitySearch.value,
          excludedRems: excludedRems,
          max: 5,
          sortBy: 'id',
          excludedAtms: true
        },
      });

      facilityOptions.value = data.data;
    } catch (error) {
      console.error(`FacilitySelect: request error`, error?.response || error);
    }
  }

  const goBackToList = () => {
    // clearForm()
    emit("goBackToList");
  };

  const addNewParticipant = () => {
    if(form.value.participants.length >= 20) return;
    form.value.participants.push({...defaulParticipantsData})
  };

  const removeParticipant = (index) => {
    form.value.participants.splice(index, 1);
  };

  const onChangeFacilitySelect = (event, index) => {
    form.value.participants[index].rems = event.rems;
    form.value.participants[index].name = event.label;
    facilityOptions.value = [];
  };

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    files.value.push(...newFiles);
    event.target.value = '';
  };

  const getFileType = (filename) => {
    if (!filename || typeof filename !== 'string') return 'file';

    const extension = filename.split('.').pop().toLowerCase();

    const types = {
      image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'ico'],
      pdf: ['pdf'],
      word: ['doc', 'docx', 'rtf', 'odt'],
      excel: ['xls', 'xlsx', 'csv', 'ods'],
      ppt: ['ppt', 'pptx', 'odp'],
      video: ['mp4', 'mkv', 'avi', 'mov', 'webm', 'wmv'],
      audio: ['mp3', 'wav', 'ogg', 'm4a'],
      zip: ['zip', 'rar', '7z', 'tar', 'gz'],
      code: ['js', 'ts', 'html', 'css', 'json', 'php', 'sql', 'go', 'py']
    };

    for (const type of Object.keys(types)) {
      if (types[type].includes(extension)) {
        return type;
      }
    }

    return 'file';
  }

  const removeFile = (index) => {
    files.value.splice(index, 1);
  };

  const getFormData = () => {
    const formData = new FormData();

    for (const file of files.value) {
      formData.append("file[]", file)
      form.value.attachedDocuments.push({name: file.name, url: ""})
    }

    formData.append("data", JSON.stringify(form.value));

    if(existingFiles.value.length) {
      formData.append(
        "existing_files",
        JSON.stringify(existingFiles.value)
      )
    }

    if(deletedFiles.value.length) {
      formData.append(
        "deleted_files",
        JSON.stringify(deletedFiles.value)
      )
    }

    return formData;
  }

  const submitForm = async () => {
    validateParticipants(form.value.participants, false);

    const formData = getFormData();

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
        "X-HTTP-Method-Override": isEdit.value ? "PUT" : "POST",
      },
    };

    disabledButton.value = true;
    try {
      isFormDisabled.value = true;
      const url = isEdit.value
         ? `v1/trainings/${formId.value}`
         : `v1/trainings`;

      /* const method = isEdit.value ? 'put' : 'post'; */

      const data = await axios.post(url, formData, config);

      contentToast(translations["operational_security.evaluation.answers_successfully"], "success", "CheckIcon")
      getLastStatus(data.data.data.last_status);
      setEditData(data.data.data);

      formId.value = data.data.data.id;
      successProcess.value = true;

      if (currentFormStatus.value === 'reopened') {
        EventBus.emit('training-validated-or-rejected', { id: formId.value, type: 'training_reopened'});
      }

      if (files.value.length) {
        files.value = []
      }
    } catch (error) {
      const errorMessage = error?.error?.response?.data?.message || translations["operational_security.evaluation.answers_error"]
      successProcess.value = false;
      contentToast(errorMessage, "danger", "AlertTriangleIcon")
    } finally {
      isFormDisabled.value = false;
      disabledButton.value = false;
    }
  };

  const getLastStatus = (trainingStatus) => {
    currentFormStatus.value = trainingStatus.status.name;
  }

  function contentToast (message, type, icon="CheckIcon") {
    toast({
      component: ToastificationContent,
      props: {
        title: message,
        icon,
        variant: type,
      },
    });
  }

  const validateForm = () => {
    errors.value = {}

    toggleCorrectiveActions()

    requiredFields.value.forEach(key => {
      const value = form.value[key]

      if (
        value === '' ||
        value === null ||
        value === undefined
      ) {
        errors.value[key] = translations["testing.penetration.required"]
      }
    })

    /* if (!form.value.facility.region || !form.value.facility.municipality || !form.value.facility.division) {
      errors.value['facility'] = translations["testing.penetration.required"]
    } */

    /* if (!files.value.length) {
      errors.value['attachedDocuments'] = translations["testing.penetration.required"]
    } */

    validateParticipants(form.value.participants)

    return Object.keys(errors.value).length === 0
  }

  const toggleCorrectiveActions = () => {
    const index = requiredFields.value.indexOf('correctiveActions')

    if (form.value.result === 'Fallido') {
      if (index === -1) {
        requiredFields.value.push('correctiveActions')
      }
    } else {
      if (index !== -1) {
        requiredFields.value.splice(index, 1)
      }
    }
  }

  const validateTopic = () => {
    if(!form.value.topicAditional.number_of_guests
      || !form.value.topicAditional.number_of_attendees
      || !form.value.topicAditional.cost
      || !form.value.topicAditional.supplier_name) {
      errors.value['topicAditional'] = translations["testing.penetration.required"]
    }
  }

  const validateParticipants = (list, showError = true) => {
    if (!list.length) {
      form.value.participants = [];
    }

    if (!list.length && showError) {
      errors.value.participants = translations["testing.penetration.required"];
      return
    }

    const hasValidUser = list.every(participant =>
      participant.rems &&
      participant.name &&
      participant.number_of_participants > 0
    )

    if (!hasValidUser && showError) {

      if (showError) {
        errors.value.participants = 'Verifique la información de los participantes';
      }
    }
  }

  const setEditData = (data) => {
    isEdit.value = true;
    formId.value = data.id;
    referenceShowed.value = data.reference_number;

    getLastStatus(data.last_status);

    form.value = {
      id_center: data.id_center,
      id_sector: data.id_sector,
      id_region: data.id_region,
      reference_number: data.reference_number,
      registration_date: data.registration_date,
      training_type: data.training_type,
      training_modality: data.training_modality,
      training_topic: data.training_topic,
      description_training: data.description_training,
      participants: data.units.map(unit => ({
        rems: unit.unit.rems,
        name: `${unit.unit.sirh ?? ''}-${unit.unit.description}`,
        number_of_participants: unit.participants
      })),
      attachedDocuments: [],
      lastStatus: data.last_status,
      trainingValidations: data.training_validations
    }

    existingFiles.value = data.files || [];

    const disabledByStatuses = ['approved','in_review','rejected','canceled'];
    const owenerUserTesting  = (user.id === form.value.lastStatus.id_user);

    if (disabledByStatuses.includes(currentFormStatus.value) || !owenerUserTesting) {
      isFormDisabled.value = true;
    }

    if (currentFormStatus.value === 'in_progress' || currentFormStatus.value === 'in_review') {
      successProcess.value = true;
    }
    //currentFormStatus.value = data.status
    // form.value = {"rems":{"rems":456,"sirh":654,"facility":{"value":14022,"label":"654-Tests28OCT","categories":[{"id":5,"name":"ATM","description":"ATM"}],"region":"Ciudad De México","subregion":"Sector (Test1)","sirh":654,"municipality":"Alcaldía. Benito Juárez","rems":456,"id_seproban":null}},"trainingDate":"01/02/2026","responsibleOfExercise":"Rafa","type":"Ingeniería Social","description":"test","eventType":"Prueba","detailedDescription":"dsf","securityParticipants":[{"name":"dfsa","profile":"asd","soeid":""},{"name":"gg","profile":"gg","soeid":""}],"additionalParticipants":[{"name":"asdf","profile":"asdf","soeid":""},{"name":"222","profile":"222","soeid":""}],"employeesToEvaluated":[{"name":"uyyuuy","profile":"uyuuy","soeid":""}],"result":"Fallido","correctiveActions":"fthdft","suggestedActions":"sdfgsdf","attachedDocuments":[{"name":"acuse_cita_placas.pdf","url":""},{"name":"github-logo.png","url":""}],"approver":"Developer"}
    // existingFiles.value = [{"name":"acuse_cita_placas.pdf","url":""},{"name":"github-logo.png","url":""}]
  }

  const removeExistingFile = (index) => {
    deletedFiles.value.push(existingFiles.value[index]);
    existingFiles.value.splice(index, 1);
  };

  const getValidators = async () => {
    await axios.get('v1/trainings/user-validators')
      .then(response => {
        userOptions.value = response.data.data
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.message ?? translations["operational_security.evaluation.status_error"];
        contentToast(errorMessage, "danger", "AlertTriangleIcon")
      });
  };

  const getUsersReopened = async () => {
    await axios.get('v1/trainings/user-reopened')
      .then(response => {
        userReopenedOptions.value = response.data.data
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.message ?? translations["operational_security.evaluation.status_error"];
        contentToast(errorMessage, "danger", "AlertTriangleIcon")
      });
  };

  const sendToReview = async () => {
      if (!validateForm()) return;

      const formData = getFormData();

      const config = {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }

      formData.append('id_training', formId.value);
      formData.append('id_user_validation', userSelected.value);

      disabledButton.value = true;

      await axios.post('v1/trainings/validation', formData, config)
        .then(response => {
          const { data } = response;

          contentToast(data.message, 'success', 'CheckIcon');
          goBackToList();
        })
        .finally(() => {
          disabledButton.value = false;
        });
    }


  const approve = () => {
    errorValidationComment.value = null;

    if (!validationComment.value) {
      errorValidationComment.value = translations["testing.penetration.required"];
      return;
    }

    const request = {
      id_training: formId.value,
      comments: validationComment.value
    }

    disabledButton.value = true;

    axios.post('v1/trainings/approve', request)
      .then(response => {
        const { data } = response;

        EventBus.emit('training-validated-or-rejected', { id: formId.value, type: 'training_validate'});
        contentToast(data.message, 'success', 'CheckIcon');
        goBackToList();
      })
      .finally(() => {
        disabledButton.value = false;
      });
  }

  const reject = () => {
    errorValidationComment.value = null;

    if (!validationComment.value) {
      errorValidationComment.value = translations["testing.penetration.required"];
      return;
    }

    const request = {
      id_training: formId.value,
      comments: validationComment.value
    }

    disabledButton.value = true;

    axios.post('v1/trainings/reject', request)
      .then(response => {
        const { data } = response;

        form.value.penetrationTestingStatus = data.data.penetration_testing_status;

        getLastStatus(data.data.last_status);
        EventBus.emit('training-validated-or-rejected', { id: formId.value, type: 'training_validate'});
        contentToast(data.message, 'success', 'CheckIcon');
      })
      .finally(() => {
        disabledButton.value = false;
      });
  }

  const reopen = async () => {
    const request = {
      id_training: formId.value,
      id_user_reopened: userSelectedReasigned.value
    }

    disabledButton.value = true;

    await axios.post('v1/trainings/reopen', request)
      .then(response => {
        const { data } = response;

        contentToast(data.message, 'success', 'CheckIcon');
        goBackToList();
      })
      .finally(() => {
        disabledButton.value = false;
      });
  }

  async function getFacilityByRems(rems, index) {
    try {
      const {data} = await axios.get('/v1/unit', {
        params: {
          q: rems,
          'with[]': 'municipality',
        },
      });
      if(data?.data[0]?.rems) {
        form.value.participants[index].name = data?.data[0]?.description
      } else {
        form.value.participants[index].name = ''
      }
    } catch (error) {
      console.error(`request error`, error?.response || error);
    }
  }

  const onSearchByRems = debounce(async (rems, index) => {
    if(!rems) {
      form.value.participants[index].name = ''
      return;
    }
    await getFacilityByRems(rems, index);
  }, 800);

  async function getUsers(q) {
    try {
      const {data} = await axios.get('/v1/users', {
        params: {
          max: 5,
          q
        },
      });
      approverOptions.value = data.data;
    } catch (error) {
      console.error(`request error`, error?.response || error);
    }
  }

  const onSearchUser = debounce(async (query, loading) => {
    loading(true);
    await getUsers(query);
    loading(false);
  }, 500);

  const getStatusVariant = (status) => {
    if (!status) return "secondary";

    const statusVariants = {
      in_progress: "primary",
      pending: "warning",
      in_review: "info",
      completed: "success",
      rejected: "danger",
      reopened: "warning",
      approved: "success"
    };

    return statusVariants[status] || "secondary";
  }

  const getStatusLabel = (status) => {
    if (!status) return "-";

    return t(`operational_security.evaluation.status.${status}`);
  }

  const statusesByValidator = ['approved','canceled','rejected','in_review'];

  function formatUser(s) {
    const { name:nameStatus } = s.status;
    const historicalUser      = (statusesByValidator.includes(nameStatus)) ? s.user_validation : s.user;
    const name                = (historicalUser) ? historicalUser.user : '-';
    const email               = (historicalUser) ? historicalUser.email : '-';

    return [name, email].filter(Boolean).join(' | ');
  }

  function getUserName(s) {
    const { name:nameStatus } = s.status;
    const historicalUser      = (statusesByValidator.includes(nameStatus)) ? s.user_validation : s.user;

    return (historicalUser) ? historicalUser.user : '-';
  }

  function getUserEmail(s) {
    const { name:nameStatus } = s.status;
    const historicalUser      = (statusesByValidator.includes(nameStatus)) ? s.user_validation : s.user;

    return (historicalUser) ? historicalUser.email : '-';
  }

  function formatDate(iso) {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  }

  return {
    form,
    isDark,
    formattedFacilityOptions,
    isFormDisabled,
    datePickerConfig,
    typeTrainingOptions,
    trainingModalityOptions,
    trainingTopicsOptions,
    resultsOptions,
    eventTypeOptions,
    referenceShowed,
    formStatus,
    canUserSendToReview,
    existingFiles,
    validationComment,
    errors,
    currentFacilitySelected,
    onSearchFacility,
    files,
    formattedUserOptions,
    goBackToList,
    fetchFacilityData,
    addNewParticipant,
    removeParticipant,
    onChangeFacilitySelect,
    handleFileChange,
    removeFile,
    submitForm,
    setEditData,
    removeExistingFile,
    getValidators,
    userOptions,
    userSelected,
    userSelectedReasigned,
    sendToReview,
    approve,
    reject,
    showAlert,
    userReopenedOptions,
    getUsersReopened,
    reopen,
    errorValidationComment,
    onSearchByRems,
    getUsers,
    onSearchUser,
    getStatusVariant,
    getStatusLabel,
    formatUser,
    getUserName,
    getUserEmail,
    formatDate,
    user,
    formId,
    disabledButton
  }
}
